// The "Material selection basis" chips — how an option picks (and prices) its materials.
//
// Extracted from EstimateEditorModal so the SAME control serves both moments it belongs to:
//   * creating an option ("GAF via ABC Supply" is what makes a second option worth comparing, so
//     the basis is part of naming it — not something to go back and set afterwards), and
//   * editing one, where changing the basis re-prices the lines already on the estimate.
//
// Deliberately CONTROLLED and action-free: the two call sites commit differently — create passes
// `generation_method` to the generate endpoint, edit POSTs it to .../reprice — so the parent owns
// the button and renders it as `children`. Pulling the Apply action in here would have forced one
// of them to fake the other's shape.
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type GenerationMethod,
  useGetManufacturersForVersionQuery,
  useGetSuppliersForVersionQuery,
} from '@/redux/internal-services/estimatesApi';

// The basis vocabulary is EstimateOption.generation_method's own enum (see
// app/domain/estimates/models.py) — not a UI-local list, so a value chosen here round-trips to the
// field the pricing engine reads.
export type BasisCode = 'PREFERRED_SUPPLIER' | 'PREFERRED_MANUFACTURER' | 'LOWEST_COST';

export const BASIS_LABELS = (t: (k: string, d: string) => string): Record<BasisCode, string> => ({
  PREFERRED_SUPPLIER: t('estimates.editor.basisPreferredSupplier', 'Preferred supplier'),
  PREFERRED_MANUFACTURER: t('estimates.editor.basisPreferredManufacturer', 'Preferred manufacturer'),
  LOWEST_COST: t('estimates.editor.basisLowestCost', 'Lowest cost'),
});

/** The `generation_method` these three pieces of state describe — the exact shape both the
 *  generate and the reprice endpoint take, so neither call site hand-rolls it. */
export function buildGenerationMethod(
  basis: BasisCode, supplierCode: string | null, manufacturerCode: string | null,
): GenerationMethod {
  return {
    method_code: basis,
    parameters:
      basis === 'PREFERRED_SUPPLIER' ? { supplier_code: supplierCode }
        : basis === 'PREFERRED_MANUFACTURER' ? { manufacturer_code: manufacturerCode }
          : {},
  };
}

export interface MaterialBasisPickerProps {
  /** Where the supplier/manufacturer candidates are read from. Both endpoints are version-scoped
   *  (they answer "who can price THESE materials"), so when creating an option — which has no
   *  version yet — pass the sibling option's version: a new option is generated from the same WBS
   *  package against the same roof, so it resolves the same material classes. The coverage counts
   *  are therefore the sibling's, and exact only while the packages agree. */
  versionCode: string;
  basis: BasisCode;
  onBasisChange: (basis: BasisCode) => void;
  supplierCode: string | null;
  onSupplierChange: (supplierCode: string | null) => void;
  manufacturerCode: string | null;
  onManufacturerChange: (manufacturerCode: string | null) => void;
  /** The editor folds this away (an option runs to ~30 lines and the basis is not what you came
   *  for); the create dialog has nothing else in it, so it stays open and loses the header. */
  collapsible?: boolean;
  /** The parent's commit control — "Apply" when editing, nothing when creating (the dialog's own
   *  Create button carries it). */
  children?: ReactNode;
}

export default function MaterialBasisPicker({
  versionCode, basis, onBasisChange, supplierCode, onSupplierChange,
  manufacturerCode, onManufacturerChange, collapsible = true, children,
}: MaterialBasisPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const expanded = collapsible ? open : true;

  // The suppliers who carry something ALREADY on this estimate — scoped to the current items
  // rather than the whole supplier_product_master, which runs to thousands of rows and would list
  // suppliers who cannot price a single line here.
  const { data: suppliers, isLoading: loadingSuppliers } =
    useGetSuppliersForVersionQuery(versionCode, { skip: !versionCode });
  // Wider scope than the suppliers above, deliberately: a manufacturer basis changes WHICH item
  // is specified, so the candidates come from the material classes' catalogue. Scoping it to the
  // manufacturers already on the estimate would offer GAF and Generic — the one list that cannot
  // express the choice being made.
  const { data: manufacturers, isLoading: loadingManufacturers } =
    useGetManufacturersForVersionQuery(versionCode, { skip: !versionCode });

  const chip = (active: boolean) =>
    `rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
      active
        ? 'border-accent bg-accent-fill text-on-accent'
        : 'border-accent-soft bg-ui-card text-accent-text hover:bg-ui-hover'
    }`;

  return (
    <div className="rounded-xl border border-accent-soft bg-accent-soft/15 px-3 py-2.5">
      {collapsible && (
        <button
          type="button" onClick={() => setOpen((v) => !v)}
          className="flex w-full items-start justify-between gap-2 text-left"
        >
          <span>
            <span className="block text-xs font-semibold text-accent-text">
              {t('estimates.editor.basisTitle', 'Material selection basis')}
            </span>
            <span className="mt-0.5 block text-xs text-ui-text2">{BASIS_LABELS(t)[basis]}</span>
          </span>
          <span className="text-ui-text3">{open ? '▴' : '▾'}</span>
        </button>
      )}

      {expanded && (
        <div className={collapsible ? 'mt-2.5 space-y-2.5' : 'space-y-2.5'}>
          <div>
            <p className="text-xs text-ui-text3">{t('estimates.editor.basis', 'Basis')}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(['PREFERRED_SUPPLIER', 'PREFERRED_MANUFACTURER', 'LOWEST_COST'] as BasisCode[]).map((code) => (
                <button
                  key={code} type="button" onClick={() => onBasisChange(code)}
                  className={chip(basis === code)}
                >
                  {BASIS_LABELS(t)[code]}
                </button>
              ))}
            </div>
          </div>

          {/* Choosing a supplier is a PRICING choice, not an item swap: the estimate keeps
              the item the WBS template resolved and buys it from the named supplier. A
              supplier rarely carries every line, so the count says how far they reach —
              everything else keeps its default price source. */}
          {basis === 'PREFERRED_SUPPLIER' && (
            <div>
              <p className="text-xs text-ui-text3">{t('estimates.editor.supplier', 'Supplier')}</p>
              {/* `undefined` means STILL LOADING, not "none". Collapsing the two printed
                  "no supplier price list covers this estimate" for the second before the
                  response landed — a definitive statement about the tenant's data, made
                  while the answer was still in flight. */}
              {loadingSuppliers ? (
                <p className="mt-1 text-xs text-ui-text3">{t('estimates.editor.loading', 'Loading…')}</p>
              ) : suppliers && suppliers.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {suppliers.map((sup) => (
                    <button
                      key={sup.supplier_code} type="button"
                      onClick={() => onSupplierChange(
                        supplierCode === sup.supplier_code ? null : sup.supplier_code,
                      )}
                      className={chip(supplierCode === sup.supplier_code)}
                    >
                      {sup.supplier_name}
                      <span className="ml-1 font-normal opacity-70">
                        {sup.line_count}/{sup.total_items}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1 rounded-lg bg-ui-subtle px-2.5 py-2 text-xs text-ui-text2">
                  {t(
                    'estimates.editor.noSuppliersForItems',
                    'No supplier price list covers the materials on this estimate yet. Import and map one under Inventory to price an option from a specific supplier.',
                  )}
                </p>
              )}
            </div>
          )}

          {/* Choosing a manufacturer is an ITEM choice — GAF's shingle for Owens
              Corning's — so the count is how many of this estimate's material classes that
              manufacturer makes something for. Classes they do not cover keep the item
              already specified. */}
          {basis === 'PREFERRED_MANUFACTURER' && (
            <div>
              <p className="text-xs text-ui-text3">{t('estimates.editor.manufacturer', 'Manufacturer')}</p>
              {loadingManufacturers ? (
                <p className="mt-1 text-xs text-ui-text3">{t('estimates.editor.loading', 'Loading…')}</p>
              ) : manufacturers && manufacturers.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {manufacturers.map((mfr) => {
                    // They make a product for none of the materials on this estimate, so
                    // selecting them could not change a single line. Shown disabled with the
                    // reason rather than hidden — vanishing from the list reads as missing data.
                    const locked = mfr.material_count === 0;
                    return (
                      <button
                        key={mfr.manufacturer_code} type="button" disabled={locked}
                        title={locked
                          ? t('estimates.editor.noMatchingProduct', 'They make no product for the materials on this estimate')
                          : undefined}
                        onClick={() => onManufacturerChange(
                          manufacturerCode === mfr.manufacturer_code ? null : mfr.manufacturer_code,
                        )}
                        className={locked
                          ? 'cursor-not-allowed rounded-lg border border-ui-border bg-ui-subtle px-2.5 py-1 text-xs font-medium text-ui-text3'
                          : chip(manufacturerCode === mfr.manufacturer_code)}
                      >
                        {mfr.manufacturer_code}
                        <span className="ml-1 font-normal opacity-70">
                          {locked
                            ? t('estimates.editor.noProduct', 'no product')
                            : `${mfr.material_count}/${mfr.total_materials}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-1 rounded-lg bg-ui-subtle px-2.5 py-2 text-xs text-ui-text2">
                  {t(
                    'estimates.editor.noManufacturersForClasses',
                    'No catalogue manufacturer covers the material classes on this estimate.',
                  )}
                </p>
              )}
            </div>
          )}

          {children}
        </div>
      )}
    </div>
  );
}
